// stores/warikan-store.js
import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";
import {
  createGuestWarikan,
  fetchGuestExchangeRates,
  fetchGuestWarikanMembers,
  fetchGuestWarikans,
  isGuestMode,
  upsertGuestExchangeRate,
} from "@/utils/guest-session";
import { buildExchangeRateMap, normalizeCurrency } from "@/utils/exchange-rate";

export const useWarikanStore = defineStore("warikan", {
  state: () => ({
    warikans: [],
    warikanMembers: [],
    settlementStatuses: [],
    exchangeRates: [],
    exchangeRateMap: { JPY: 1 },
    isLoading: false,
    error: null,
  }),
  actions: {
    setExchangeRates(rates) {
      this.exchangeRates = Array.isArray(rates) ? rates : [];
      this.exchangeRateMap = buildExchangeRateMap(this.exchangeRates);
    },

    async fetchExchangeRates(shioriId) {
      this.error = null;
      try {
        if (isGuestMode()) {
          const rates = fetchGuestExchangeRates(shioriId);
          this.setExchangeRates(rates);
          return rates;
        }
        const response = await axios.get(
          `${API_URL}/shiori/${shioriId}/exchange-rates`,
        );
        this.setExchangeRates(response.data);
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("為替レート取得エラー:", err);
        throw err;
      }
    },

    async upsertExchangeRate(shioriId, currency, rateToJpy, rateDate = null) {
      this.error = null;
      const normalizedCurrency = normalizeCurrency(currency);
      try {
        if (isGuestMode()) {
          const row = upsertGuestExchangeRate(shioriId, {
            currency: normalizedCurrency,
            rateToJpy,
            rateDate,
          });
          this.setExchangeRates(fetchGuestExchangeRates(shioriId));
          return row;
        }
        const response = await axios.put(
          `${API_URL}/shiori/${shioriId}/exchange-rates/${normalizedCurrency}`,
          {
            currency: normalizedCurrency,
            rateToJpy,
            rateDate,
            source: "manual",
          },
          withShioriAccessContext(shioriId),
        );
        await this.fetchExchangeRates(shioriId);
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("為替レート保存エラー:", err);
        throw err;
      }
    },

    async refreshExchangeRates(shioriId, currencies) {
      this.error = null;
      const normalizedCurrencies = (currencies || [])
        .map((currency) => normalizeCurrency(currency))
        .filter((currency) => currency !== "JPY");
      if (isGuestMode()) {
        return this.fetchExchangeRates(shioriId);
      }
      const response = await axios.post(
        `${API_URL}/shiori/${shioriId}/exchange-rates/refresh`,
        { currencies: normalizedCurrencies },
        withShioriAccessContext(shioriId),
      );
      await this.fetchExchangeRates(shioriId);
      return response.data;
    },

    async fetchSharedExchangeRates(shareToken) {
      this.error = null;
      const response = await axios.get(
        `${API_URL}/shiori/shared/${shareToken}/exchange-rates`,
      );
      this.setExchangeRates(response.data);
      return response.data;
    },

    async upsertSharedExchangeRate(shareToken, currency, rateToJpy, rateDate = null) {
      const normalizedCurrency = normalizeCurrency(currency);
      const response = await axios.put(
        `${API_URL}/shiori/shared/${shareToken}/exchange-rates/${normalizedCurrency}`,
        {
          currency: normalizedCurrency,
          rateToJpy,
          rateDate,
          source: "manual",
        },
      );
      await this.fetchSharedExchangeRates(shareToken);
      return response.data;
    },

    async refreshSharedExchangeRates(shareToken, currencies) {
      const normalizedCurrencies = (currencies || [])
        .map((currency) => normalizeCurrency(currency))
        .filter((currency) => currency !== "JPY");
      const response = await axios.post(
        `${API_URL}/shiori/shared/${shareToken}/exchange-rates/refresh`,
        { currencies: normalizedCurrencies },
      );
      await this.fetchSharedExchangeRates(shareToken);
      return response.data;
    },

    async fetchWarikans(shioriId) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          this.warikans = fetchGuestWarikans(shioriId);
          return this.warikans;
        }

        const response = await axios.get(
          `${API_URL}/warikan/?shiori_id=${shioriId}`,
        );
        this.warikans = response.data;
      } catch (err) {
        this.error = err;
        console.error("割り勘取得エラー:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchWarikanMembers(shioriId) {
      this.error = null;
      try {
        if (isGuestMode()) {
          this.warikanMembers = fetchGuestWarikanMembers(shioriId);
          return this.warikanMembers;
        }

        const response = await axios.get(
          `${API_URL}/warikan_member/?shiori_id=${shioriId}`,
        );
        this.warikanMembers = response.data;
      } catch (err) {
        this.error = err;
        console.error("割り勘メンバー取得エラー:", err);
      }
    },

    async fetchSettlementStatuses(shioriId) {
      this.error = null;
      try {
        if (isGuestMode()) {
          this.settlementStatuses = [];
          return this.settlementStatuses;
        }

        const response = await axios.get(
          `${API_URL}/warikan_settlement_status/?shiori_id=${shioriId}`,
        );
        this.settlementStatuses = response.data;
      } catch (err) {
        this.error = err;
        console.error("精算ステータス取得エラー:", err);
      }
    },

    async updateSettlementStatus(shioriId, settlement, settled) {
      this.error = null;
      const response = await axios.put(
        `${API_URL}/warikan_settlement_status/${shioriId}`,
        {
          fromMemberCode: settlement.fromCode,
          toMemberCode: settlement.toCode,
          currency: "JPY",
          amount: settlement.amount,
          settled,
        },
        withShioriAccessContext(shioriId),
      );
      await this.fetchSettlementStatuses(shioriId);
      return response.data;
    },

    async updateExpenseSettlementStatus(shioriId, expense, settled) {
      this.error = null;
      const response = await axios.put(
        `${API_URL}/expense_settlement_status/${shioriId}`,
        {
          expenseType: expense.type,
          lineNon: expense.lineNon,
          settled,
        },
        withShioriAccessContext(shioriId),
      );
      if (expense.type === "schedule") {
        const target = expense.source;
        if (target) target.EXPENSE_SETTLED_FLG = settled ? "1" : "0";
      } else {
        const target = this.warikans.find(
          (item) => item.LINE_NON === expense.lineNon,
        );
        if (target) target.EXPENSE_SETTLED_FLG = settled ? "1" : "0";
      }
      return response.data;
    },

    async createWarikan(warikanData) {
      this.isLoading = true;
      this.error = null;
      try {
        if (isGuestMode()) {
          const memberCodes = (warikanData.members || [])
            .map((member) => member.member_code || member.MEMBER_CODE)
            .filter(Boolean);
          const result = createGuestWarikan(warikanData.shioriId, {
            subject: warikanData.subject,
            amount: warikanData.amount,
            currency: warikanData.currency,
            payerCode: warikanData.paidBy,
            memberCodes,
            memoText: warikanData.memoText,
          });
          await this.fetchWarikans(warikanData.shioriId);
          await this.fetchWarikanMembers(warikanData.shioriId);
          await this.fetchSettlementStatuses(warikanData.shioriId);
          return result;
        }

        const response = await axios.post(
          `${API_URL}/warikan/`,
          warikanData,
          withShioriAccessContext(warikanData.shioriId),
        );
        // 作成成功後、リストを再取得
        if (warikanData.shioriId) {
          await this.fetchWarikans(warikanData.shioriId);
          await this.fetchWarikanMembers(warikanData.shioriId);
          await this.fetchSettlementStatuses(warikanData.shioriId);
        }
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("割り勘作成エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async createSharedWarikan(shareToken, warikanData) {
      const response = await axios.post(
        `${API_URL}/shiori/shared/${shareToken}/warikan`,
        warikanData,
      );
      return response.data;
    },

    async updateSharedWarikan(shareToken, lineNon, warikanData) {
      const response = await axios.put(
        `${API_URL}/shiori/shared/${shareToken}/warikan/${lineNon}`,
        warikanData,
      );
      return response.data;
    },

    async deleteSharedWarikan(shareToken, lineNon) {
      const response = await axios.delete(
        `${API_URL}/shiori/shared/${shareToken}/warikan/${lineNon}`,
      );
      return response.data;
    },

    async updateSharedSettlementStatus(shareToken, settlement, settled) {
      const response = await axios.put(
        `${API_URL}/shiori/shared/${shareToken}/warikan-settlement-status`,
        {
          fromMemberCode: settlement.fromCode,
          toMemberCode: settlement.toCode,
          currency: "JPY",
          amount: settlement.amount,
          settled,
        },
      );
      return response.data;
    },

    async updateSharedExpenseSettlementStatus(
      shareToken,
      expense,
      settled,
    ) {
      const response = await axios.put(
        `${API_URL}/shiori/shared/${shareToken}/expense-settlement-status`,
        {
          expenseType: expense.type,
          lineNon: expense.lineNon,
          settled,
        },
      );
      return response.data;
    },

    async updateWarikan(shioriId, lineNon, warikanData) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.put(
          `${API_URL}/warikan/${shioriId}/${lineNon}`,
          warikanData,
          withShioriAccessContext(shioriId),
        );
        // 更新成功後、リストを再取得
        await this.fetchWarikans(shioriId);
        await this.fetchWarikanMembers(shioriId);
        await this.fetchSettlementStatuses(shioriId);
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("割り勘更新エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteWarikan(shioriId, lineNon) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.delete(
          `${API_URL}/warikan/${shioriId}/${lineNon}`,
          withShioriAccessContext(shioriId),
        );
        // 削除成功後、リストを再取得
        await this.fetchWarikans(shioriId);
        await this.fetchWarikanMembers(shioriId);
        await this.fetchSettlementStatuses(shioriId);
        return response.data;
      } catch (err) {
        this.error = err;
        console.error("割り勘削除エラー:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },
    clearWarikanData() {
      this.warikans = [];
      this.warikanMembers = [];
      this.settlementStatuses = [];
      this.setExchangeRates([]);
      this.error = null;
    },
  },
});
